import { c as _c } from "react/compiler-runtime";
import Head from 'next/head';
import Script from 'next/script';
import { scrubCliAuthFromPostHogEvent } from '@hookdeck/analytics';
import { Analytics as DubAnalytics } from '@dub/analytics/react';
import { StyleSheetManager } from 'styled-components';
import ThemeProvider, { styleSheetManagerProps } from '@hookdeck/theme';
import loadGTM from '../client/GTM';
import LiveChatUnavailableToast from '../components/common/LiveChatUnavailableToast';
import { ToastProvider } from '../components/common/Toast';
import { GlobalContextProvider } from '../components/contexts/GlobalContext';
import { UserContextProvider } from '../components/contexts/UserContext';
import posthog from 'posthog-js';
import { PostHogProvider } from 'posthog-js/react';

// Check that PostHog is client-side (used to handle Next.js SSR)
if (typeof window !== 'undefined') {
  const query = new URLSearchParams(window.location.search);
  const postHogDistinctId = query.get('pid') || undefined;
  posthog.init(process.env.POSTHOG_API_KEY || '', {
    api_host: process.env.POSTHOG_HOST || 'https://app.posthog.com',
    person_profiles: 'identified_only',
    before_send: event => scrubCliAuthFromPostHogEvent(event),
    integrations: {
      crispChat: true,
      // Typing issue with the SDK, adding this to please ts
      intercom: false
    },
    bootstrap: {
      distinctID: postHogDistinctId
    },
    // Enable debug mode in development
    loaded: instance => {
      window.posthog = instance as unknown as typeof posthog;
      if (process.env.NODE_ENV === 'development') instance.debug();
    }
  });

  // Google Tag Manager — no-op in development or without GTM_ID (see client/GTM.ts).
  loadGTM();
}

/** Hookdeck properties Dub decorates with the click id (dub_id) for cross-domain attribution. */
const DUB_OUTBOUND_DOMAINS = ['hookdeck.com', 'dashboard.hookdeck.com', 'console.hookdeck.com'];
const MyApp = t0 => {
  const $ = _c(41);
  const {
    Component,
    pageProps
  } = t0;
  let t1;
  if ($[0] !== Component.configs) {
    t1 = Component.configs || {};
    $[0] = Component.configs;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const {
    title,
    desc,
    keywords
  } = t1;
  let t2;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <LiveChatUnavailableToast />;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = {
      APP_URL: process.env.APP_URL,
      API_URL: process.env.UI_API_URL ?? process.env.API_URL,
      OUTPOST_API_URL: process.env.OUTPOST_API_URL,
      SENTRY_DSN: process.env.DASHBOARD_SENTRY_DSN,
      ROOT_DOMAIN: process.env.ROOT_DOMAIN,
      WEBSITE_URL: process.env.WEBSITE_URL,
      ENV: process.env.ENV,
      LONG_TIMEOUT_ATTEMPT_ROUTING_ENABLED: process.env.LONG_TIMEOUT_ATTEMPT_ROUTING_ENABLED === "true"
    };
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  let t4;
  if ($[4] !== Component || $[5] !== pageProps) {
    t4 = <ThemeProvider><ToastProvider>{t2}<GlobalContextProvider configs={t3}><UserContextProvider><PostHogProvider client={posthog}><Component {...pageProps} /></PostHogProvider></UserContextProvider></GlobalContextProvider></ToastProvider></ThemeProvider>;
    $[4] = Component;
    $[5] = pageProps;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  const app_tree = t4;
  let t5;
  let t6;
  let t7;
  let t8;
  if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = process.env.NODE_ENV === "development" && <style>{"nextjs-portal { display: none !important; }"}</style>;
    t6 = <meta charSet="utf-8" />;
    t7 = <meta name="viewport" content="width=device-width, initial-scale=1.0" />;
    t8 = <meta httpEquiv="x-ua-compatible" content="ie=edge" />;
    $[7] = t5;
    $[8] = t6;
    $[9] = t7;
    $[10] = t8;
  } else {
    t5 = $[7];
    t6 = $[8];
    t7 = $[9];
    t8 = $[10];
  }
  const t9 = `${title} - Hookdeck`;
  let t10;
  if ($[11] !== t9) {
    t10 = <title>{t9}</title>;
    $[11] = t9;
    $[12] = t10;
  } else {
    t10 = $[12];
  }
  let t11;
  if ($[13] !== desc) {
    t11 = desc && <meta name="description" content={desc} />;
    $[13] = desc;
    $[14] = t11;
  } else {
    t11 = $[14];
  }
  let t12;
  if ($[15] !== keywords) {
    t12 = keywords && keywords.length > 0 && <meta name="keywords" content={keywords.join(",")} />;
    $[15] = keywords;
    $[16] = t12;
  } else {
    t12 = $[16];
  }
  const t13 = `${title} - Hookdeck`;
  let t14;
  if ($[17] !== t13) {
    t14 = <meta property="og:title" content={t13} />;
    $[17] = t13;
    $[18] = t14;
  } else {
    t14 = $[18];
  }
  let t15;
  if ($[19] !== desc) {
    t15 = desc && <meta name="description" content={desc} />;
    $[19] = desc;
    $[20] = t15;
  } else {
    t15 = $[20];
  }
  let t16;
  let t17;
  let t18;
  let t19;
  let t20;
  let t21;
  let t22;
  if ($[21] === Symbol.for("react.memo_cache_sentinel")) {
    t16 = <link rel="apple-touch-icon" sizes="180x180" href="/images/favicon/apple-touch-icon.png" />;
    t17 = <link rel="icon" type="image/png" sizes="32x32" href="/images/favicon/favicon-32x32.png" />;
    t18 = <link rel="icon" type="image/png" sizes="16x16" href="/images/favicon/favicon-16x16.png" />;
    t19 = <link rel="manifest" href="/site.webmanifest" />;
    t20 = <link rel="mask-icon" href="/images/favicon/safari-pinned-tab.svg" color="#5bbad5" />;
    t21 = <meta name="msapplication-TileColor" content="#2b5797" />;
    t22 = <meta name="theme-color" content="#ffffff" />;
    $[21] = t16;
    $[22] = t17;
    $[23] = t18;
    $[24] = t19;
    $[25] = t20;
    $[26] = t21;
    $[27] = t22;
  } else {
    t16 = $[21];
    t17 = $[22];
    t18 = $[23];
    t19 = $[24];
    t20 = $[25];
    t21 = $[26];
    t22 = $[27];
  }
  let t23;
  if ($[28] !== t10 || $[29] !== t11 || $[30] !== t12 || $[31] !== t14 || $[32] !== t15) {
    t23 = <Head>{t5}{t6}{t7}{t8}{t10}{t11}{t12}{t14}{t15}{t16}{t17}{t18}{t19}{t20}{t21}{t22}</Head>;
    $[28] = t10;
    $[29] = t11;
    $[30] = t12;
    $[31] = t14;
    $[32] = t15;
    $[33] = t23;
  } else {
    t23 = $[33];
  }
  let t24;
  let t25;
  if ($[34] === Symbol.for("react.memo_cache_sentinel")) {
    t24 = <Script dangerouslySetInnerHTML={{
      __html: `
          window.$crisp=[];window.CRISP_WEBSITE_ID="${process.env.CRISP_WEBSITE_ID}";(function(){d=document;s=d.createElement("script");s.src="https://client.crisp.chat/l.js";s.async=1;d.getElementsByTagName("head")[0].appendChild(s);})();
          $crisp.push(["safe", true])
          $crisp.push(["do", "chat:hide"])
          $crisp.push(["on", "chat:closed", function() { $crisp.push(["do", "chat:hide"]) }])
          $crisp.push(["on", "message:received", function() { $crisp.push(["do", "chat:show"]) }])
          window.CRISP_READY_TRIGGER = function() {
            var unread = $crisp.get("chat:unread:count")
            if (unread > 0) {
              $crisp.push(["do", "chat:show"])
            } else {
              $crisp.push(["do", "chat:hide"])
            }
          };
        `
    }} />;
    t25 = process.env.ENV !== "development" && <DubAnalytics apiHost="/_proxy/dub" scriptProps={{
      src: "/_proxy/dub/script.js"
    }} domainsConfig={{
      outbound: DUB_OUTBOUND_DOMAINS
    }} cookieOptions={{
      domain: `.${process.env.ROOT_DOMAIN}`
    }} />;
    $[34] = t24;
    $[35] = t25;
  } else {
    t24 = $[34];
    t25 = $[35];
  }
  let t26;
  if ($[36] !== app_tree) {
    t26 = typeof window === "undefined" ? app_tree : <StyleSheetManager {...styleSheetManagerProps}>{app_tree}</StyleSheetManager>;
    $[36] = app_tree;
    $[37] = t26;
  } else {
    t26 = $[37];
  }
  let t27;
  if ($[38] !== t23 || $[39] !== t26) {
    t27 = <>{t23}{t24}{t25}{t26}</>;
    $[38] = t23;
    $[39] = t26;
    $[40] = t27;
  } else {
    t27 = $[40];
  }
  return t27;
};
export default MyApp;